"use client";
import * as React from "react";
import { DataTable, type Column } from "../DataTable";
import { PageHeader, StatCard, Card2 } from "../ModuleHelpers";
import { useERP, formatBDT } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Receipt, FileCheck, Download, Printer } from "lucide-react";
import { CSVExportButton, PrintButton, type PrintData } from "../PrintExport";

export function VATTaxModule() {
  const erp = useERP();
  const { toast } = useToast();
  const [filedReturns, setFiledReturns] = React.useState<Set<string>>(new Set(["Q1-2026"]));

  const vatLedger = erp.salesInvoices.map(inv => ({
    id: inv.id, invoice: inv.invoiceNumber, customer: erp.getCustomer(inv.customerId)?.name ?? "—",
    date: inv.invoiceDate, taxable: inv.amount, vat: inv.tax, total: inv.total, status: inv.status,
  }));
  const totalVAT = erp.salesInvoices.reduce((s, i) => s + i.tax, 0);
  const inputVAT = 84500;
  const netPayable = totalVAT - inputVAT;

  // Quarterly returns
  const quarters = [
    { id: "Q1-2026", period: "Q1 2026 (Jan-Mar)", vat: Math.round(totalVAT * 0.25), status: filedReturns.has("Q1-2026") ? "Filed" : "Pending" },
    { id: "Q2-2026", period: "Q2 2026 (Apr-Jun)", vat: Math.round(totalVAT * 0.25), status: filedReturns.has("Q2-2026") ? "Filed" : "Pending" },
    { id: "Q3-2026", period: "Q3 2026 (Jul-Sep)", vat: Math.round(totalVAT * 0.25), status: filedReturns.has("Q3-2026") ? "Filed" : "Pending" },
    { id: "Q4-2026", period: "Q4 2026 (Oct-Dec)", vat: Math.round(totalVAT * 0.25), status: filedReturns.has("Q4-2026") ? "Filed" : "Pending" },
  ];

  const handleFileReturn = (qId: string, period: string, amount: number) => {
    // C4 fix: use returned JE id, post directly
    const jeId = erp.createJE({
      date: new Date().toISOString().slice(0, 10),
      description: `VAT return filed for ${period}`,
      lines: [
        { account: "VAT Payable", debit: amount, credit: 0 },
        { account: "Bank Account", debit: 0, credit: amount },
      ],
    });
    erp.postJE(jeId, "Rumana Begum");
    setFiledReturns(prev => new Set([...prev, qId]));
    toast({ title: "VAT Return Filed", description: `${period} filed with NBR. VAT paid: ${formatBDT(amount)}. JE posted.` });
  };

  const handleExportMushak = (qId: string) => {
    toast({ title: "Mushak Generated", description: `${qId} Mushak 9.1 form exported as XML for NBR submission.` });
  };

  const printData: PrintData = {
    title: "VAT Ledger Report",
    docNumber: `VAT-${new Date().toISOString().slice(0, 7)}`,
    docDate: new Date().toISOString().slice(0, 10),
    columns: [
      { key: "invoice", label: "Invoice #" },
      { key: "customer", label: "Customer" },
      { key: "date", label: "Date" },
      { key: "taxable", label: "Taxable", align: "right" },
      { key: "vat", label: "VAT", align: "right" },
      { key: "total", label: "Total", align: "right" },
    ],
    rows: vatLedger.map(r => ({ ...r, taxable: formatBDT(r.taxable), vat: formatBDT(r.vat), total: formatBDT(r.total) })),
    totals: [
      { label: "Total VAT Collected", value: formatBDT(totalVAT) },
      { label: "Input VAT", value: formatBDT(inputVAT) },
      { label: "Net VAT Payable", value: formatBDT(netPayable) },
    ],
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="VAT & Tax Compliance" desc="Bangladesh NBR-compliant VAT ledger and return preparation." icon={Receipt} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total VAT Collected" value={formatBDT(totalVAT)} tone="text-primary" />
        <StatCard label="VAT Returns Filed" value={`${filedReturns.size} / 4`} />
        <StatCard label="Input VAT" value={formatBDT(inputVAT)} />
        <StatCard label="Net VAT Payable" value={formatBDT(netPayable)} tone="text-destructive" />
      </div>

      <Card2 title="Quarterly VAT Returns">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {quarters.map(q => (
            <div key={q.id} className="rounded-md border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{q.period}</span>
                <Badge variant={q.status === "Filed" ? "default" : "secondary"}>{q.status}</Badge>
              </div>
              <div className="text-lg font-semibold text-destructive">{formatBDT(q.vat)}</div>
              <div className="flex gap-1">
                {q.status === "Pending" && (
                  <Button size="sm" className="h-7 text-xs flex-1" onClick={() => handleFileReturn(q.id, q.period, q.vat)}><FileCheck className="size-3 mr-1" />File Return</Button>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleExportMushak(q.id)}><Download className="size-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card2>

      <Card2 title="VAT Ledger">
        <div className="mb-3 flex justify-end gap-2">
          <CSVExportButton columns={[{key:"invoice",label:"Invoice"},{key:"customer",label:"Customer"},{key:"date",label:"Date"},{key:"taxable",label:"Taxable"},{key:"vat",label:"VAT"},{key:"total",label:"Total"}]} rows={vatLedger.map(r => ({...r, taxable: String(r.taxable), vat: String(r.vat), total: String(r.total)}))} filename="vat-ledger.csv" />
          <PrintButton data={printData} label="Print Ledger" />
        </div>
        <DataTable
          columns={[
            { key: "invoice", header: "Invoice #", sortable: true, render: r => <span className="font-mono text-xs">{r.invoice}</span> },
            { key: "customer", header: "Customer" },
            { key: "date", header: "Date" },
            { key: "taxable", header: "Taxable Amount", align: "right", sortable: true, render: r => formatBDT(r.taxable) },
            { key: "vat", header: "VAT (15%)", align: "right", render: r => formatBDT(r.vat) },
            { key: "total", header: "Total", align: "right", render: r => formatBDT(r.total) },
            { key: "status", header: "Status", render: r => <Badge variant="secondary">{r.status}</Badge> },
          ] as Column<typeof vatLedger[number]>[]}
          data={vatLedger}
          rowKey={r => r.id}
          searchKeys={["invoice", "customer"]}
        />
      </Card2>
    </div>
  );
}
